import React, { useState, useEffect, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { 
  Calendar as CalendarIcon, 
  Plus, 
  ChevronLeft, 
  ChevronRight,
  Users,
  Clock,
  MapPin,
  User,
  UserPlus,
  Trash2,
  Eye,
  Check,
  X,
  GraduationCap,
  Shield
} from 'lucide-react';
import api from '@/services/api';
import { useApp } from '@/context/AppContext';
import { toast } from 'sonner';

const DAYS = ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'];
const MONTHS = ['Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin', 'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'];

const STATUT_COLORS = {
  planifie: 'bg-blue-100 text-blue-800',
  en_cours: 'bg-yellow-100 text-yellow-800',
  termine: 'bg-green-100 text-green-800',
  annule: 'bg-red-100 text-red-800'
};

const STATUT_LABELS = {
  planifie: 'Planifié',
  en_cours: 'En cours',
  termine: 'Terminé',
  annule: 'Annulé'
};

export default function PlanningFormations() {
  const { currentUser, typesFormations: ctxTypesFormations, categoriesCACES: ctxCategoriesCACES, collaborateurs: ctxCollaborateurs } = useApp();
  const [typesFormations, setTypesFormations] = useState([]);
  const [categoriesCACES, setCategoriesCACES] = useState([]);
  const [collaborateurs, setCollaborateurs] = useState([]);
  const [plannings, setPlannings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [viewMode, setViewMode] = useState('month'); // month, list
  
  // Dialogs
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [inscriptionDialogOpen, setInscriptionDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedPlanning, setSelectedPlanning] = useState(null);
  
  // Forms
  const [planningType, setPlanningType] = useState('formation'); // 'formation' or 'caces'
  const [planningForm, setPlanningForm] = useState({
    type_formation_id: '',
    categorie_id: '',
    date: '',
    date_fin: '', // For CACES validity
    heure_debut: '09:00',
    heure_fin: '17:00',
    lieu: '',
    formateur: '',
    organisme: '',
    places_max: 10
  });
  
  const [selectedCollaborateur, setSelectedCollaborateur] = useState('');
  
  const isHSE = currentUser?.role === 'HSE' || currentUser?.role === 'Admin';
  const isManager = currentUser?.role === 'Manager';

  // Load reference data
  useEffect(() => {
    const loadReferenceData = async () => {
      try {
        if (ctxTypesFormations && ctxTypesFormations.length > 0) {
          setTypesFormations(ctxTypesFormations);
        } else {
          const data = await api.getTypesFormations();
          setTypesFormations(data || []);
        }
        
        if (ctxCategoriesCACES && ctxCategoriesCACES.length > 0) {
          setCategoriesCACES(ctxCategoriesCACES);
        } else {
          const data = await api.getCategoriesCACES();
          setCategoriesCACES(data || []);
        }
        
        if (ctxCollaborateurs && ctxCollaborateurs.length > 0) {
          setCollaborateurs(ctxCollaborateurs);
        } else {
          const data = await api.getCollaborateurs();
          setCollaborateurs(data || []);
        }
      } catch (error) {
        console.error('Error loading reference data:', error);
      }
    };
    loadReferenceData();
  }, [ctxTypesFormations, ctxCategoriesCACES, ctxCollaborateurs]);

  useEffect(() => {
    loadPlannings();
  }, []);

  const loadPlannings = async () => {
    try {
      setLoading(true);
      const [formations, caces] = await Promise.all([
        api.getPlanningFormations(),
        api.getPlanningCACES ? api.getPlanningCACES() : Promise.resolve([])
      ]);
      // Combine both types with a type indicator
      const allPlannings = [
        ...formations.map(p => ({ ...p, planning_type: 'formation' })),
        ...caces.map(p => ({ ...p, planning_type: 'caces' }))
      ];
      setPlannings(allPlannings);
    } catch (error) {
      toast.error('Erreur lors du chargement du planning');
    } finally {
      setLoading(false);
    }
  };

  // Filter collaborateurs based on manager's team
  const availableCollaborateurs = useMemo(() => {
    if (isManager && currentUser?.secteur_id) {
      return collaborateurs.filter(c => c.secteur_id === currentUser.secteur_id);
    }
    return collaborateurs;
  }, [collaborateurs, isManager, currentUser]);

  // Calendar helpers
  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDay = (firstDay.getDay() + 6) % 7; // Monday = 0
    
    const days = [];
    
    // Previous month days
    const prevMonth = new Date(year, month, 0);
    for (let i = startingDay - 1; i >= 0; i--) {
      days.push({
        date: new Date(year, month - 1, prevMonth.getDate() - i),
        isCurrentMonth: false
      });
    }
    
    // Current month days
    for (let i = 1; i <= daysInMonth; i++) {
      days.push({
        date: new Date(year, month, i),
        isCurrentMonth: true
      });
    }
    
    // Next month days
    const remainingDays = 42 - days.length;
    for (let i = 1; i <= remainingDays; i++) {
      days.push({
        date: new Date(year, month + 1, i),
        isCurrentMonth: false
      });
    }
    
    return days;
  };

  // Format date to YYYY-MM-DD in local timezone (avoid UTC conversion issues)
  const formatDateLocal = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const getPlanningsForDate = (date) => {
    const dateStr = formatDateLocal(date);
    return plannings.filter(p => p.date === dateStr);
  };

  const navigateMonth = (direction) => {
    setCurrentDate(prev => {
      const newDate = new Date(prev);
      newDate.setMonth(newDate.getMonth() + direction);
      return newDate;
    });
  };

  const handleCreatePlanning = async (e) => {
    e.preventDefault();
    try {
      if (planningType === 'formation') {
        await api.createPlanningFormation(
          planningForm,
          currentUser.id,
          `${currentUser.prenom} ${currentUser.nom}`,
          currentUser.email
        );
        toast.success('Formation planifiée avec succès');
      } else {
        // Create CACES planning
        await api.createPlanningCACES(
          planningForm,
          currentUser.id,
          `${currentUser.prenom} ${currentUser.nom}`,
          currentUser.email
        );
        toast.success('Formation CACES planifiée avec succès');
      }
      setCreateDialogOpen(false);
      resetPlanningForm();
      loadPlannings();
    } catch (error) {
      toast.error(error.message || 'Erreur lors de la création');
    }
  };

  const handleDeletePlanning = async () => {
    if (!selectedPlanning) return;
    try {
      if (selectedPlanning.planning_type === 'caces') {
        await api.deletePlanningCACES(selectedPlanning.id);
      } else {
        await api.deletePlanningFormation(selectedPlanning.id);
      }
      toast.success('Formation supprimée');
      setDeleteDialogOpen(false);
      setDetailDialogOpen(false);
      setSelectedPlanning(null);
      loadPlannings();
    } catch (error) {
      toast.error(error.message || 'Erreur lors de la suppression');
    }
  };

  const handleInscription = async () => {
    if (!selectedPlanning || !selectedCollaborateur) return;
    try {
      // Utiliser la bonne méthode selon le type de planning
      if (selectedPlanning.planning_type === 'caces') {
        await api.createInscriptionCACES(
          selectedPlanning.id,
          { planning_id: selectedPlanning.id, collaborateur_id: selectedCollaborateur },
          currentUser.id,
          `${currentUser.prenom} ${currentUser.nom}`
        );
      } else {
        await api.createInscription(
          selectedPlanning.id,
          { planning_id: selectedPlanning.id, collaborateur_id: selectedCollaborateur },
          currentUser.id,
          `${currentUser.prenom} ${currentUser.nom}`
        );
      }
      toast.success('Inscription enregistrée');
      setInscriptionDialogOpen(false);
      setSelectedCollaborateur('');
      
      // Reload planning detail
      if (selectedPlanning.planning_type === 'caces') {
        const updated = await api.getPlanningCACESDetail(selectedPlanning.id);
        setSelectedPlanning(updated);
      } else {
        const updated = await api.getPlanningFormation(selectedPlanning.id);
        setSelectedPlanning(updated);
      }
      loadPlannings();
    } catch (error) {
      toast.error(error.message || 'Erreur lors de l\'inscription');
    }
  };

  const handleCancelInscription = async (inscriptionId) => {
    try {
      // Utiliser la bonne méthode selon le type de planning
      if (selectedPlanning.planning_type === 'caces') {
        await api.deleteInscriptionCACES(inscriptionId);
      } else {
        await api.deleteInscription(inscriptionId);
      }
      toast.success('Inscription annulée');
      
      // Reload planning detail
      if (selectedPlanning.planning_type === 'caces') {
        const updated = await api.getPlanningCACESDetail(selectedPlanning.id);
        setSelectedPlanning(updated);
      } else {
        const updated = await api.getPlanningFormation(selectedPlanning.id);
        setSelectedPlanning(updated);
      }
      loadPlannings();
    } catch (error) {
      toast.error('Erreur lors de l\'annulation');
    }
  };

  const openPlanningDetail = async (planning) => {
    try {
      let detailed;
      if (planning.planning_type === 'caces') {
        detailed = await api.getPlanningCACES ? 
          await api.getPlanningCACESDetail(planning.id) : 
          planning;
      } else {
        detailed = await api.getPlanningFormation(planning.id);
      }
      setSelectedPlanning({ ...detailed, planning_type: planning.planning_type });
      setDetailDialogOpen(true);
    } catch (error) {
      toast.error('Erreur lors du chargement');
    }
  };

  const resetPlanningForm = () => {
    setPlanningForm({
      type_formation_id: '',
      categorie_id: '',
      date: '',
      date_fin: '',
      heure_debut: '09:00',
      heure_fin: '17:00',
      lieu: '',
      formateur: '',
      organisme: '',
      places_max: 10
    });
    setPlanningType('formation');
  };
  
  // Calculate CACES validity date (5 years by default)
  const calculateCACESEndDate = (startDate) => {
    if (!startDate) return '';
    const date = new Date(startDate);
    date.setFullYear(date.getFullYear() + 5);
    return formatDateLocal(date);
  };

  const calendarDays = getDaysInMonth(currentDate);
  
  const today = formatDateLocal(new Date());

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Planning des Formations & CACES</h1>
          <p className="text-gray-500">
            {isHSE ? 'Planifiez et gérez les sessions de formation et CACES' : 'Inscrivez votre équipe aux formations'}
          </p>
        </div>
        <div className="flex gap-2">
          <Select value={viewMode} onValueChange={setViewMode}>
            <SelectTrigger className="w-32">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="month">Calendrier</SelectItem>
              <SelectItem value="list">Liste</SelectItem>
            </SelectContent>
          </Select>
          {isHSE && (
            <Button onClick={() => setCreateDialogOpen(true)} className="gap-2">
              <Plus className="h-4 w-4" />
              Planifier
            </Button>
          )}
        </div>
      </div>

      {/* Calendar View */}
      {viewMode === 'month' && (
        <Card>
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <Button variant="ghost" size="icon" onClick={() => navigateMonth(-1)}>
                <ChevronLeft className="h-5 w-5" />
              </Button>
              <CardTitle className="text-lg">
                {MONTHS[currentDate.getMonth()]} {currentDate.getFullYear()}
              </CardTitle>
              <Button variant="ghost" size="icon" onClick={() => navigateMonth(1)}>
                <ChevronRight className="h-5 w-5" />
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {/* Days header */}
            <div className="grid grid-cols-7 gap-1 mb-2">
              {DAYS.map(day => (
                <div key={day} className="text-center text-sm font-medium text-gray-500 py-2">
                  {day}
                </div>
              ))}
            </div>
            
            {/* Calendar grid */}
            <div className="grid grid-cols-7 gap-1">
              {calendarDays.map((day, idx) => {
                const dateStr = formatDateLocal(day.date);
                const dayPlannings = getPlanningsForDate(day.date);
                const isToday = dateStr === today;
                
                return (
                  <div
                    key={idx}
                    className={`min-h-24 p-1 border rounded-lg ${
                      day.isCurrentMonth ? 'bg-white' : 'bg-gray-50'
                    } ${isToday ? 'border-blue-500 border-2' : 'border-gray-200'}`}
                  >
                    <div className={`text-sm font-medium mb-1 ${
                      day.isCurrentMonth ? 'text-gray-900' : 'text-gray-400'
                    } ${isToday ? 'text-blue-600' : ''}`}>
                      {day.date.getDate()}
                    </div>
                    <div className="space-y-1">
                      {dayPlannings.slice(0, 2).map(planning => (
                        <div
                          key={planning.id}
                          onClick={() => openPlanningDetail(planning)}
                          className={`text-xs p-1 rounded cursor-pointer truncate ${
                            planning.planning_type === 'caces' 
                              ? 'bg-amber-100 text-amber-800 border-l-2 border-amber-500' 
                              : STATUT_COLORS[planning.statut] || 'bg-gray-100'
                          }`}
                        >
                          {planning.planning_type === 'caces' && <span className="font-bold">🚜 </span>}
                          {planning.heure_debut} - {planning.type_formation_code || planning.categorie_code || planning.intitule}
                        </div>
                      ))}
                      {dayPlannings.length > 2 && (
                        <div className="text-xs text-gray-500 text-center">
                          +{dayPlannings.length - 2} autres
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* List View */}
      {viewMode === 'list' && (
        <Card>
          <CardContent className="p-0">
            {loading ? (
              <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              </div>
            ) : plannings.length === 0 ? (
              <div className="text-center py-12">
                <CalendarIcon className="h-12 w-12 mx-auto text-gray-400 mb-4" />
                <p className="text-gray-500">Aucune formation planifiée</p>
              </div>
            ) : (
              <div className="divide-y">
                {plannings.map(planning => (
                  <div
                    key={planning.id}
                    className="p-4 hover:bg-gray-50 cursor-pointer"
                    onClick={() => openPlanningDetail(planning)}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          {planning.planning_type === 'caces' ? (
                            <Badge className="bg-amber-100 text-amber-800">CACES</Badge>
                          ) : (
                            <Badge className="bg-blue-100 text-blue-800">Formation</Badge>
                          )}
                          <Badge variant="outline">{planning.type_formation_code || planning.categorie_code}</Badge>
                          <span className="font-medium">{planning.intitule || planning.categorie_libelle}</span>
                          <Badge className={STATUT_COLORS[planning.statut]}>
                            {STATUT_LABELS[planning.statut]}
                          </Badge>
                        </div>
                        <div className="flex items-center gap-4 text-sm text-gray-500">
                          <span className="flex items-center gap-1">
                            <CalendarIcon className="h-4 w-4" />
                            {planning.date}
                          </span>
                          <span className="flex items-center gap-1">
                            <Clock className="h-4 w-4" />
                            {planning.heure_debut} - {planning.heure_fin}
                          </span>
                          {planning.lieu && (
                            <span className="flex items-center gap-1">
                              <MapPin className="h-4 w-4" />
                              {planning.lieu}
                            </span>
                          )}
                          <span className="flex items-center gap-1">
                            <Users className="h-4 w-4" />
                            {planning.nb_inscrits || 0}/{planning.places_max} inscrits
                          </span>
                        </div>
                      </div>
                      <ChevronRight className="h-5 w-5 text-gray-400" />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Create Planning Dialog (HSE only) */}
      <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Planifier une formation</DialogTitle>
          </DialogHeader>
          
          {/* Type selector */}
          <Tabs value={planningType} onValueChange={setPlanningType} className="w-full">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="formation" className="gap-2">
                <GraduationCap className="h-4 w-4" />
                Formation
              </TabsTrigger>
              <TabsTrigger value="caces" className="gap-2">
                <Shield className="h-4 w-4" />
                CACES
              </TabsTrigger>
            </TabsList>
          </Tabs>
          
          <form onSubmit={handleCreatePlanning}>
            <div className="space-y-4 py-4">
              {/* Formation type selector */}
              {planningType === 'formation' ? (
                <div className="space-y-2">
                  <Label>Type de formation *</Label>
                  <Select 
                    value={planningForm.type_formation_id} 
                    onValueChange={(v) => setPlanningForm({ ...planningForm, type_formation_id: v })}
                    required
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Sélectionner un type" />
                    </SelectTrigger>
                    <SelectContent>
                      {typesFormations.map(type => (
                        <SelectItem key={type.id} value={type.id}>
                          <div className="flex items-center gap-2">
                            <Badge variant="outline" className="text-xs">{type.code}</Badge>
                            {type.intitule}
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              ) : (
                <div className="space-y-2">
                  <Label>Catégorie CACES *</Label>
                  <Select 
                    value={planningForm.categorie_id} 
                    onValueChange={(v) => setPlanningForm({ ...planningForm, categorie_id: v })}
                    required
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Sélectionner une catégorie" />
                    </SelectTrigger>
                    <SelectContent>
                      {categoriesCACES?.map(cat => (
                        <SelectItem key={cat.id} value={cat.id}>
                          <div className="flex items-center gap-2">
                            <Badge variant="outline" className="text-xs bg-amber-50">{cat.code}</Badge>
                            {cat.libelle}
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Date {planningType === 'caces' ? 'de formation' : ''} *</Label>
                  <Input
                    type="date"
                    value={planningForm.date}
                    onChange={(e) => {
                      const newDate = e.target.value;
                      setPlanningForm({ 
                        ...planningForm, 
                        date: newDate,
                        // Auto-calculate CACES end date (5 years)
                        date_fin: planningType === 'caces' ? calculateCACESEndDate(newDate) : ''
                      });
                    }}
                    required
                  />
                </div>
                {planningType === 'caces' ? (
                  <div className="space-y-2">
                    <Label>Date de fin de validité *</Label>
                    <Input
                      type="date"
                      value={planningForm.date_fin}
                      onChange={(e) => setPlanningForm({ ...planningForm, date_fin: e.target.value })}
                      required
                    />
                  </div>
                ) : (
                  <div className="space-y-2">
                    <Label>Places max *</Label>
                    <Input
                      type="number"
                      min="1"
                      value={planningForm.places_max}
                      onChange={(e) => setPlanningForm({ ...planningForm, places_max: parseInt(e.target.value) })}
                      required
                    />
                  </div>
                )}
              </div>
              
              {planningType === 'caces' && (
                <div className="space-y-2">
                  <Label>Places max *</Label>
                  <Input
                    type="number"
                    min="1"
                    value={planningForm.places_max}
                    onChange={(e) => setPlanningForm({ ...planningForm, places_max: parseInt(e.target.value) })}
                    required
                  />
                </div>
              )}
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Heure début *</Label>
                  <Input
                    type="time"
                    value={planningForm.heure_debut}
                    onChange={(e) => setPlanningForm({ ...planningForm, heure_debut: e.target.value })}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label>Heure fin *</Label>
                  <Input
                    type="time"
                    value={planningForm.heure_fin}
                    onChange={(e) => setPlanningForm({ ...planningForm, heure_fin: e.target.value })}
                    required
                  />
                </div>
              </div>
              
              <div className="space-y-2">
                <Label>Lieu</Label>
                <Input
                  value={planningForm.lieu}
                  onChange={(e) => setPlanningForm({ ...planningForm, lieu: e.target.value })}
                  placeholder="Salle de formation, Site..."
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Formateur</Label>
                  <Input
                    value={planningForm.formateur}
                    onChange={(e) => setPlanningForm({ ...planningForm, formateur: e.target.value })}
                    placeholder="Nom du formateur"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Organisme {planningType === 'caces' ? '*' : ''}</Label>
                  <Input
                    value={planningForm.organisme}
                    onChange={(e) => setPlanningForm({ ...planningForm, organisme: e.target.value })}
                    placeholder="AFPA, AFTRAL..."
                    required={planningType === 'caces'}
                  />
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setCreateDialogOpen(false)}>
                Annuler
              </Button>
              <Button type="submit">Planifier</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Planning Detail Dialog */}
      <Dialog open={detailDialogOpen} onOpenChange={setDetailDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          {selectedPlanning && (
            <>
              <DialogHeader>
                <div className="flex items-center justify-between">
                  <DialogTitle className="flex items-center gap-2">
                    {selectedPlanning.planning_type === 'caces' ? (
                      <Badge className="bg-amber-100 text-amber-800">CACES</Badge>
                    ) : (
                      <Badge className="bg-blue-100 text-blue-800">Formation</Badge>
                    )}
                    <Badge variant="outline">{selectedPlanning.type_formation_code || selectedPlanning.categorie_code}</Badge>
                    {selectedPlanning.intitule || selectedPlanning.categorie_libelle}
                  </DialogTitle>
                  <Badge className={STATUT_COLORS[selectedPlanning.statut]}>
                    {STATUT_LABELS[selectedPlanning.statut]}
                  </Badge>
                </div>
              </DialogHeader>
              
              <div className="space-y-6 py-4">
                {/* Info */}
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div className="flex items-center gap-2">
                    <CalendarIcon className="h-4 w-4 text-gray-400" />
                    <span>{selectedPlanning.date}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Clock className="h-4 w-4 text-gray-400" />
                    <span>{selectedPlanning.heure_debut} - {selectedPlanning.heure_fin}</span>
                  </div>
                  {selectedPlanning.lieu && (
                    <div className="flex items-center gap-2">
                      <MapPin className="h-4 w-4 text-gray-400" />
                      <span>{selectedPlanning.lieu}</span>
                    </div>
                  )}
                  {selectedPlanning.formateur && (
                    <div className="flex items-center gap-2">
                      <User className="h-4 w-4 text-gray-400" />
                      <span>{selectedPlanning.formateur}</span>
                    </div>
                  )}
                </div>
                
                {/* Places */}
                <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div className="flex items-center gap-2">
                    <Users className="h-5 w-5 text-gray-600" />
                    <span className="font-medium">Places</span>
                  </div>
                  <div className="text-right">
                    <span className="text-2xl font-bold">{selectedPlanning.nb_inscrits || 0}</span>
                    <span className="text-gray-500">/{selectedPlanning.places_max}</span>
                    <p className="text-xs text-gray-500">
                      {selectedPlanning.places_restantes > 0 
                        ? `${selectedPlanning.places_restantes} places restantes`
                        : 'Complet'
                      }
                    </p>
                  </div>
                </div>
                
                {/* Inscrits */}
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="font-medium">Inscrits</h3>
                    {(isManager || isHSE) && selectedPlanning.places_restantes > 0 && selectedPlanning.statut === 'planifie' && (
                      <Button size="sm" onClick={() => setInscriptionDialogOpen(true)} className="gap-1">
                        <UserPlus className="h-4 w-4" />
                        Inscrire
                      </Button>
                    )}
                  </div>
                  
                  {selectedPlanning.inscriptions?.length > 0 ? (
                    <div className="space-y-2">
                      {selectedPlanning.inscriptions.map(insc => (
                        <div 
                          key={insc.id} 
                          className="flex items-center justify-between p-2 bg-white border rounded-lg"
                        >
                          <div>
                            <p className="font-medium">{insc.collaborateur_nom}</p>
                            <p className="text-xs text-gray-500">
                              {insc.collaborateur_matricule} • Inscrit par {insc.inscrit_par_nom}
                            </p>
                          </div>
                          <div className="flex items-center gap-2">
                            <Badge variant={insc.statut === 'annule' ? 'secondary' : 'outline'}>
                              {insc.statut}
                            </Badge>
                            {(isManager || isHSE) && insc.statut !== 'annule' && (
                              <Button 
                                variant="ghost" 
                                size="icon"
                                onClick={() => handleCancelInscription(insc.id)}
                              >
                                <X className="h-4 w-4 text-red-500" />
                              </Button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-gray-500 text-center py-4">Aucun inscrit</p>
                  )}
                </div>
              </div>
              
              <DialogFooter>
                {isHSE && selectedPlanning.statut === 'planifie' && (
                  <Button 
                    variant="destructive" 
                    onClick={() => setDeleteDialogOpen(true)}
                    className="mr-auto"
                  >
                    <Trash2 className="h-4 w-4 mr-2" />
                    Supprimer
                  </Button>
                )}
                <Button variant="outline" onClick={() => setDetailDialogOpen(false)}>
                  Fermer
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Inscription Dialog */}
      <Dialog open={inscriptionDialogOpen} onOpenChange={setInscriptionDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Inscrire un collaborateur</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <Label>Collaborateur *</Label>
            <Select value={selectedCollaborateur} onValueChange={setSelectedCollaborateur}>
              <SelectTrigger className="mt-2">
                <SelectValue placeholder="Sélectionner un collaborateur" />
              </SelectTrigger>
              <SelectContent>
                {availableCollaborateurs
                  .filter(c => !selectedPlanning?.inscriptions?.some(i => i.collaborateur_id === c.id && i.statut !== 'annule'))
                  .map(collab => (
                    <SelectItem key={collab.id} value={collab.id}>
                      {collab.prenom} {collab.nom} ({collab.matricule})
                    </SelectItem>
                  ))
                }
              </SelectContent>
            </Select>
            {isManager && (
              <p className="text-xs text-gray-500 mt-2">
                Seuls les collaborateurs de votre équipe sont affichés
              </p>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setInscriptionDialogOpen(false)}>
              Annuler
            </Button>
            <Button onClick={handleInscription} disabled={!selectedCollaborateur}>
              Inscrire
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirmer la suppression</AlertDialogTitle>
            <AlertDialogDescription>
              Êtes-vous sûr de vouloir supprimer cette formation planifiée ?
              Cette action est irréversible.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeletePlanning} className="bg-red-600 hover:bg-red-700">
              Supprimer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
